import React from 'react';

const Card = ({
  children,
  className = '',
  padding = 'md',
  shadow = 'sm',
  border = true,
  rounded = 'lg',
  background = 'white',
  hover = false,
  onClick,
  ...props
}) => {
  // Padding styles
  const paddingStyles = {
    none: '',
    xs: 'p-2',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10'
  };

  // Shadow styles
  const shadowStyles = {
    none: '',
    xs: 'shadow-xs',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl'
  };

  // Rounded styles
  const roundedStyles = {
    none: '',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full'
  };

  // Background styles using CSS variables
  const backgroundStyles = {
    white: 'bg-card',
    gray: 'bg-muted',
    transparent: 'bg-transparent',
    gradient: 'bg-card'
  };

  const cardClasses = `
    ${backgroundStyles[background]}
    ${border ? 'border border-border' : ''}
    ${shadowStyles[shadow]}
    ${roundedStyles[rounded]}
    ${paddingStyles[padding]}
    ${hover ? 'transition-all duration-200 hover:shadow-md hover:scale-[1.02]' : ''}
    ${onClick ? 'cursor-pointer' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div 
      className={cardClasses}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

// Card Header Component
export const CardHeader = ({ 
  title, 
  subtitle, 
  action, 
  className = '',
  titleClassName = '',
  subtitleClassName = ''
}) => {
  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          {title && (
            <h3 className={`text-lg font-semibold text-foreground ${titleClassName}`}>
              {title}
            </h3>
          )}
          {subtitle && (
            <p className={`text-sm text-muted-foreground mt-1 ${subtitleClassName}`}>
              {subtitle}
            </p>
          )}
        </div>
        {action && (
          <div className="flex-shrink-0 ml-4">
            {action}
          </div>
        )}
      </div>
    </div>
  );
};

// Card Body Component
export const CardBody = ({ children, className = '' }) => {
  return (
    <div className={className}>
      {children}
    </div>
  );
};

// Card Footer Component
export const CardFooter = ({ children, className = '', divider = true }) => {
  return (
    <div className={`
      ${divider ? 'border-t border-border pt-4 mt-6' : ''}
      ${className}
    `}>
      {children}
    </div>
  );
};

// Stat Card Component
export const StatCard = ({
  title,
  value,
  change,
  changeType = 'percentage',
  trend = 'neutral',
  icon,
  subtitle,
  className = '',
  onClick
}) => {
  const trendColors = {
    positive: 'text-success',
    negative: 'text-destructive',
    neutral: 'text-muted-foreground'
  };

  const trendIcons = {
    positive: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 17l10-10M17 7v10M17 7H7" />
      </svg>
    ),
    negative: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17l-10-10M7 7v10M7 7h10" />
      </svg>
    ),
    neutral: null
  };

  return (
    <Card 
      className={`${className}`}
      hover={!!onClick}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            {icon && (
              <div className="p-2 bg-primary/10 rounded-lg">
                {React.cloneElement(icon, { 
                  className: "w-5 h-5 text-primary" 
                })}
              </div>
            )}
            <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
          </div>
          
          <div className="mb-2">
            <span className="text-2xl font-bold text-foreground">{value}</span>
          </div>

          {change !== undefined && (
            <div className={`flex items-center space-x-1 text-sm ${trendColors[trend]}`}>
              {trendIcons[trend]}
              <span>
                {changeType === 'percentage' ? `${Math.abs(change)}%` : change}
                {trend === 'positive' ? ' increase' : trend === 'negative' ? ' decrease' : ''}
              </span>
            </div>
          )}

          {subtitle && (
            <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
          )}
        </div>
      </div>
    </Card>
  );
};

// Info Card Component
export const InfoCard = ({
  title,
  description,
  icon,
  variant = 'default',
  className = ''
}) => {
  const variants = {
    default: 'bg-info/10 border-info/30 text-info',
    success: 'bg-success/10 border-success/30 text-success',
    warning: 'bg-warning/10 border-warning/30 text-warning',
    error: 'bg-destructive/10 border-destructive/30 text-destructive',
    orange: 'bg-primary/10 border-primary/30 text-primary'
  };

  return (
    <Card 
      className={`${variants[variant]} ${className}`}
      padding="md"
      shadow="none"
    >
      <div className="flex items-start space-x-3">
        {icon && (
          <div className="flex-shrink-0">
            {React.cloneElement(icon, { className: "w-5 h-5" })}
          </div>
        )}
        <div className="flex-1">
          {title && (
            <h4 className="font-medium mb-1">{title}</h4>
          )}
          {description && (
            <p className="text-sm opacity-90">{description}</p>
          )}
        </div>
      </div>
    </Card>
  );
};

// Feature Card Component
export const FeatureCard = ({
  title,
  description,
  icon,
  action,
  className = '',
  iconClassName = ''
}) => {
  return (
    <Card 
      className={`text-center ${className}`}
      hover={!!action}
      onClick={action}
      padding="lg"
    >
      {icon && (
        <div className={`w-12 h-12 mx-auto mb-4 bg-primary/10 rounded-lg flex items-center justify-center ${iconClassName}`}>
          {React.cloneElement(icon, { 
            className: "w-6 h-6 text-primary" 
          })}
        </div>
      )}
      
      {title && (
        <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      )}
      
      {description && (
        <p className="text-muted-foreground text-sm">{description}</p>
      )}
    </Card>
  );
};

export default Card;